import React from 'react';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import { FaMapMarkerAlt, FaPhone, FaEnvelope, FaWhatsapp, FaClock } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { useState } from 'react';

const LocationMap = () => {
  const [showInfoWindow, setShowInfoWindow] = useState(false);
  
  // Rudrapur coordinates
  const location = {
    lat: 28.988247,
    lng: 79.376254
  };
  
  const mapStyles = {
    height: "400px",
    width: "100%",
    borderRadius: "0.5rem"
  };

  // Custom marker icon
  const customMarker = {
    path: "M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z",
    fillColor: "#e53e3e",
    fillOpacity: 1,
    strokeWeight: 2,
    strokeColor: "#ffffff",
    scale: 2,
    anchor: { x: 12, y: 24 },
  };

  return (
    <motion.div 
      className="bg-white dark:bg-secondary-700 rounded-lg shadow-md p-6 h-full"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <h2 className="text-2xl font-semibold mb-6 text-secondary-900 dark:text-white">Our Location</h2>
      
      <div className="mb-6 shadow-md rounded-lg overflow-hidden">
        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            mapContainerStyle={mapStyles}
            zoom={15}
            center={location}
            options={{
              styles: [
                {
                  featureType: "all",
                  elementType: "labels.text.fill",
                  stylers: [{ color: "#7c93a3" }, { lightness: -10 }]
                },
                {
                  featureType: "administrative.country",
                  elementType: "geometry",
                  stylers: [{ visibility: "on" }]
                },
                {
                  featureType: "administrative.country",
                  elementType: "geometry.stroke",
                  stylers: [{ color: "#a0a4a5" }]
                },
                {
                  featureType: "poi",
                  elementType: "labels.icon",
                  stylers: [{ color: "#5a6673" }]
                },
                {
                  featureType: "water",
                  elementType: "geometry.fill",
                  stylers: [{ color: "#a3cdff" }]
                }
              ]
            }}
          >
            <Marker 
              position={location} 
              title="VEDUS Supply Chain Management"
              icon={customMarker}
              animation={window.google?.maps?.Animation?.DROP}
              onClick={() => setShowInfoWindow(true)}
            />
            
            {showInfoWindow && (
              <InfoWindow
                position={location}
                onCloseClick={() => setShowInfoWindow(false)}
              >
                <div className="p-2">
                  <h3 className="font-bold text-secondary-900">VEDUS Supply Chain Management</h3>
                  <p className="text-secondary-700 text-sm">
                    Plot No-2, Bhurarani Road, City One<br />
                    Rudrapur, Uttarakhand, 263153
                  </p>
                  <a 
                    href="https://www.google.com/maps/dir/?api=1&destination=28.988247,79.376254" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 text-sm font-medium mt-2 inline-block hover:underline"
                  >
                    Get Directions
                  </a>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </div>
      
      <div className="space-y-5">
        <motion.div 
          className="flex items-start"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          <div className="text-primary-600 dark:text-primary-400 mr-3 mt-1">
            <FaMapMarkerAlt size={20} />
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-white">Address</h3>
            <p className="text-secondary-600 dark:text-secondary-300">
              Plot No-2, Bhurarani Road, City One, Rudrapur, U.S Nagar, Uttarakhand, 263153
            </p>
          </div>
        </motion.div>
        
        <motion.div 
          className="flex items-start"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          <div className="text-primary-600 dark:text-primary-400 mr-3 mt-1">
            <FaClock size={20} />
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-white">Business Hours</h3>
            <p className="text-secondary-600 dark:text-secondary-300">
              Monday - Saturday: 9:00 AM - 6:00 PM<br />
            </p>
          </div>
        </motion.div>
        
        <motion.a
          href="tel:+919411305200"
          className="flex items-start group"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          <div className="text-primary-600 dark:text-primary-400 mr-3 mt-1 group-hover:text-primary-700 dark:group-hover:text-primary-300">
            <FaPhone size={20} />
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">Phone</h3>
            <p className="text-secondary-600 dark:text-secondary-300 group-hover:text-primary-600 dark:group-hover:text-primary-400">
              +91 94113 05200
            </p>
          </div>
        </motion.a>
        
        <motion.a 
          href="mailto:Vedusscm@gmail.com"
          className="flex items-start group"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          <div className="text-primary-600 dark:text-primary-400 mr-3 mt-1 group-hover:text-primary-700 dark:group-hover:text-primary-300">
            <FaEnvelope size={20} />
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">Email</h3>
            <p className="text-secondary-600 dark:text-secondary-300 group-hover:text-primary-600 dark:group-hover:text-primary-400">
              Vedusscm@gmail.com
            </p>
          </div>
        </motion.a>
        
        <motion.a 
          href="https://wa.me/919411305200"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-start group"
          whileHover={{ x: 5 }}
          transition={{ duration: 0.2 }}
        >
          <div className="text-primary-600 dark:text-primary-400 mr-3 mt-1 group-hover:text-primary-700 dark:group-hover:text-primary-300">
            <FaWhatsapp size={20} />
          </div>
          <div>
            <h3 className="font-medium text-secondary-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400">WhatsApp</h3>
            <p className="text-secondary-600 dark:text-secondary-300 group-hover:text-primary-600 dark:group-hover:text-primary-400">
              +91 94113 05200
            </p>
          </div>
        </motion.a>
      </div>
    </motion.div>
  );
};

export default LocationMap;